import { IconActionMenuTrigger } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useRef, useState } from "react";
import { TextInput } from "risa-oasis-ui_v2";
import { ConditionsCardProps } from "../../data-model/conditionCardProps";
import ChevronDown from "../../svg/chevron-down";
import { Delete } from "../../svg/delete";
import HamburgerIcon from "../../svg/hamburger-icon";
import SettingsIcon from "../../svg/settings-icon";

type EditableHeaderField = "label" | "subtext";

const ConditionsCard = ({
  header,
  subHeader,
  children,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  className = "",
  expandableContentClassName = "",
  expandIcon,
  headerClickable = true,
  disabled = false,
  onStageSettingClick,
  onDeleteStageClick,
  editableLabel,
  onLabelChange,
  editableSubtext,
  onSubtextChange,
  subtextPlaceholder,
  headerExtra,
  headerInputKey,
  isNumberPresent = false,
  number,
  dragHandleProps,
  dropTargetProps,
  isDraggable = false,
}: ConditionsCardProps) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState(defaultExpanded);
  const [editingField, setEditingField] = useState<EditableHeaderField | null>(
    null,
  );
  const headerContentRef = useRef<HTMLDivElement>(null);

  // Only a reload/discard (a new headerInputKey) closes the editor. Depending on the
  // text itself would exit edit mode on every keystroke, since each one updates the prop.
  useEffect(() => {
    setEditingField(null);
  }, [headerInputKey]);

  useEffect(() => {
    if (editingField == null) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!headerContentRef.current?.contains(target)) {
        setEditingField(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editingField]);

  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : uncontrolledExpanded;

  const handleToggle = () => {
    if (disabled) return;
    const next = !isExpanded;
    if (!isControlled) setUncontrolledExpanded(next);
    onExpandedChange?.(next);
  };

  const iconElement = expandIcon ?? (
    <ChevronDown
      stroke="currentColor"
      className="text-primaryGray-7"
      rotate={isExpanded ? 180 : 0}
      height={20}
      width={20}
    />
  );

  return (
    <div
      className={`conditions-card rounded-md border border-primaryGray-14 bg-white shadow-sm ${isExpanded ? "overflow-visible" : "overflow-hidden"} ${className}`}
      onDragOver={dropTargetProps?.onDragOver}
      onDrop={dropTargetProps?.onDrop}
    >
      <div
        role={headerClickable && !disabled ? "button" : undefined}
        tabIndex={headerClickable && !disabled ? 0 : undefined}
        className={`conditions-card__header flex flex-row items-center gap-5 border-b border-primaryGray-14 px-4 py-3 ${
          headerClickable && !disabled
            ? "cursor-pointer select-none transition-colors hover:bg-primaryGray-16"
            : ""
        } ${isExpanded ? "border-b-primaryGray-14" : "border-b-transparent"}`}
        style={{ gap: "1.25rem" }}
        onClick={headerClickable ? handleToggle : undefined}
        onKeyDown={(e) => {
          if (
            headerClickable &&
            !disabled &&
            (e.key === "Enter" || e.key === " ")
          ) {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        {isDraggable && (
          <div
            className={`conditions-card__header-hamburger flex shrink-0 items-center ${
              dragHandleProps ? "cursor-grab active:cursor-grabbing" : ""
            }`}
            draggable={dragHandleProps?.draggable}
            onDragStart={(e) => {
              e.stopPropagation();
              dragHandleProps?.onDragStart?.(e);
            }}
            onDragEnd={(e) => {
              e.stopPropagation();
              dragHandleProps?.onDragEnd?.(e);
            }}
            onClick={(e) => dragHandleProps && e.stopPropagation()}
            role={dragHandleProps ? "button" : undefined}
            aria-label={dragHandleProps ? "Drag to reorder" : undefined}
          >
            <HamburgerIcon />
          </div>
        )}
        {isNumberPresent && (
          <div className="conditions-card__header-number flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tertiaryBlue-4 font-bold text-white">
            {number}
          </div>
        )}
        <div
          ref={headerContentRef}
          className="conditions-card__header-content flex min-w-0 flex-1 flex-col gap-[0.125rem]"
        >
          {editableLabel != null && onLabelChange != null ? (
            editingField === "label" ? (
              <TextInput
                key={`label-${headerInputKey ?? "editable"}`}
                id={`conditions-card-label-${headerInputKey ?? editableLabel}`}
                label=""
                placeholder="Rule label"
                defaultValue={editableLabel}
                onChange={(data) => onLabelChange(data.value ?? "")}
                onBlur={(data) => {
                  onLabelChange(data.value ?? "");
                  setEditingField(null);
                }}
              />
            ) : (
              <div
                className="cursor-text font-bold"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingField("label");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingField("label");
                  }
                }}
              >
                {editableLabel}
              </div>
            )
          ) : (
            <div className="font-bold">{header}</div>
          )}

          {editableSubtext != null && onSubtextChange != null ? (
            editingField === "subtext" ? (
              <TextInput
                key={`subtext-${headerInputKey ?? "editable"}`}
                id={`conditions-card-subtext-${headerInputKey ?? editableSubtext}`}
                label=""
                placeholder={subtextPlaceholder ?? "Short description"}
                defaultValue={editableSubtext}
                onChange={(data) => onSubtextChange(data.value ?? "")}
                onBlur={(data) => {
                  onSubtextChange(data.value ?? "");
                  setEditingField(null);
                }}
              />
            ) : (
              <div
                className="cursor-text text-sm font-light text-primaryGray-6"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingField("subtext");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingField("subtext");
                  }
                }}
              >
                {editableSubtext.trim() !== ""
                  ? editableSubtext
                  : (subtextPlaceholder ?? "Short description")}
              </div>
            )
          ) : subHeader != null ? (
            <div className="text-sm font-light text-primaryGray-6">
              {subHeader}
            </div>
          ) : null}
        </div>
        <div className="conditions-card__header-actions flex shrink-0 flex-row items-center gap-2">
          {headerExtra}
          {onStageSettingClick != null ? (
            <IconActionMenuTrigger
              iconSize={18}
              items={[
                {
                  icon: <SettingsIcon />,
                  id: "stage-setting",
                  label: "Stage setting",
                  onClick: () => onStageSettingClick(),
                },
                ...(onDeleteStageClick != null
                  ? [
                      {
                        icon: <Delete />,
                        id: "delete-stage",
                        label: "Delete stage",
                        onClick: () => onDeleteStageClick(),
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
            onDeleteStageClick != null && (
              <button
                type="button"
                className="cursor-pointer p-1 transition-opacity hover:opacity-80"
                aria-label="Delete rule"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteStageClick();
                }}
              >
                <Delete width={18} height={18} />
              </button>
            )
          )}
          <div
            className={`conditions-card__header-icon flex items-center justify-end ${
              !headerClickable && !disabled
                ? "cursor-pointer p-1 transition-opacity hover:opacity-80"
                : ""
            }`}
            onClick={
              !headerClickable && !disabled
                ? (e) => {
                    e.stopPropagation();
                    handleToggle();
                  }
                : undefined
            }
            role={!headerClickable && !disabled ? "button" : undefined}
            tabIndex={!headerClickable && !disabled ? 0 : undefined}
            onKeyDown={(e) => {
              if (
                !headerClickable &&
                !disabled &&
                (e.key === "Enter" || e.key === " ")
              ) {
                e.preventDefault();
                handleToggle();
              }
            }}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            {iconElement}
          </div>
        </div>
      </div>

      <div
        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }}
      >
        <div
          className={`min-h-0 ${isExpanded ? "overflow-visible" : "overflow-hidden"}`}
        >
          <div
            className={`conditions-card__content border-t border-primaryGray-14 px-4 py-3 ${expandableContentClassName} ${isExpanded ? "relative z-10" : ""}`}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConditionsCard;
